/**
 * MinuteEntryRow — one typed entry inside a set of meeting minutes.
 *
 * Every line of a minute is the same shape (some prose, a category) but only *actions*
 * carry an owner, a due date and an open/done state. Everything else — a decision, a
 * risk noted, a dependency — is a statement of fact that nobody has to chase. So the
 * ownership cluster is conditional on the category rather than always present and
 * usually blank; see CATEGORY_TAKES_OWNER in constants/meetingMinutes.
 *
 * The row renders inside the shared RepeatableRowCard shell, and drives that shell's
 * border accent and header chips from the selected category, so a long minute reads as
 * a colour-coded list without having to open each row.
 *
 * Requirement / milestone linking is only meaningful once the meeting is pinned to an
 * initiative — the parent passes empty option arrays when it isn't, and this row then
 * explains why rather than showing two dropdowns with nothing in them.
 */

import React from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import type { MinuteCategory, MinuteEntry } from '../../types/models';
import {
  MINUTE_CATEGORIES,
  MINUTE_CATEGORY_HINT,
  MINUTE_CATEGORY_ICON,
  MINUTE_CATEGORY_LABELS,
  MINUTE_CATEGORY_TONE,
  categoryTakesOwner,
} from '../../constants/meetingMinutes';
import { RepeatableRowCard } from '../../components/RepeatableRows';
import type { RowAccent } from '../../components/RepeatableRows';
import { RichTextField, isRichTextEmpty } from '../../components/RichTextField';
import { RichTextView } from '../../components/RichTextView';
import { reactSelectStyles } from '../../utils/reactSelectStyles';
import { formatDate } from '../../utils/format';

// ─── Shared option shape ──────────────────────────────────────────────────────

export interface SelectOption {
  value: string;
  label: string;
}

/** Today as YYYY-MM-DD in the viewer's own timezone — `toISOString()` would be UTC and
 *  can read a day early/late for anyone not on it, which matters for "overdue". */
export const todayIso = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

/** Strip Quill markup down to a single line of readable text. */
export const entryPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Categories worth escalating out of the minute into a tracked artefact. */
const PROMOTABLE: ReadonlySet<MinuteCategory> = new Set<MinuteCategory>(['risk', 'blocker']);

/** Categories that can be linked to initiatives — a dependency or an informational note
 *  isn't a tracked item an initiative needs to surface on its own pages. */
const LINKABLE_TO_INITIATIVES: ReadonlySet<MinuteCategory> = new Set<MinuteCategory>([
  'action',
  'decision',
  'risk',
  'blocker',
]);

/** Where a promoted artefact lives, by artefact type. Unknown types get a plain chip. */
const ARTEFACT_ROUTE: Record<string, string> = {
  risk: '/risks',
  issue: '/issues',
  incident: '/incidents',
};

/** `ids` defaults defensively: initiativeIds is brand new, so every entry stored before this
 *  field existed has no such key in DynamoDB at all - hydrate() returns those items as-is on
 *  read, it doesn't backfill defaults, so this was crashing every meeting minutes page on load
 *  in production the moment a single pre-existing entry rendered. */
const findOptions = (options: SelectOption[], ids: string[] | undefined): SelectOption[] =>
  (ids || []).map((id) => options.find((o) => o.value === id) ?? { value: id, label: id });

// ─── Row ──────────────────────────────────────────────────────────────────────

export interface MinuteEntryRowProps {
  entry: MinuteEntry;
  index: number;
  /** Patch this entry. The parent owns the entry list. */
  onChange: (changes: Partial<MinuteEntry>) => void;
  onRemove: () => void;
  /** False while a single row remains — the shell then hides its remove control. */
  canRemove: boolean;
  /** Approved minutes are a locked record: everything renders, nothing edits. */
  readOnly: boolean;
  people: SelectOption[];
  /** Empty when the meeting is not pinned to an initiative. */
  requirements: SelectOption[];
  milestones: SelectOption[];
  /** The full initiative catalog - unlike requirements/milestones, linking an entry to an
   *  initiative has nothing to do with which one (if any) the meeting itself is pinned to. */
  initiatives: SelectOption[];
  /** Raise this entry as a tracked artefact. */
  onPromote: (entry: MinuteEntry) => void;
}

export const MinuteEntryRow: React.FC<MinuteEntryRowProps> = ({
  entry,
  index,
  onChange,
  onRemove,
  canRemove,
  readOnly,
  people,
  requirements,
  milestones,
  initiatives,
  onPromote,
}) => {
  const tone = MINUTE_CATEGORY_TONE[entry.category];
  const takesOwner = categoryTakesOwner(entry.category);
  const isOverdue = takesOwner && entry.status === 'open' && !!entry.dueDate && entry.dueDate < todayIso();
  const isDone = takesOwner && entry.status === 'done';
  const artefactRoute = ARTEFACT_ROUTE[entry.promotedArtefactType];
  const canLink = requirements.length > 0 || milestones.length > 0;
  const canLinkInitiatives = LINKABLE_TO_INITIATIVES.has(entry.category) && initiatives.length > 0;

  const ownerValue = entry.ownerId
    ? people.find((p) => p.value === entry.ownerId) ?? { value: entry.ownerId, label: entry.ownerId }
    : null;

  return (
    <RepeatableRowCard
      label="Entry"
      index={index}
      accent={tone as RowAccent}
      onRemove={!readOnly && canRemove ? onRemove : undefined}
      chips={
        <>
          <span className={`np-chip np-tone-${tone}`}>
            <i className={MINUTE_CATEGORY_ICON[entry.category]}></i>
            {MINUTE_CATEGORY_LABELS[entry.category]}
          </span>

          {isDone && (
            <span className="np-chip np-tone-success">
              <i className="ri-check-line"></i>Done
            </span>
          )}

          {isOverdue && (
            <span className="np-chip np-tone-danger">
              <i className="ri-alarm-warning-line"></i>Overdue — {formatDate(entry.dueDate)}
            </span>
          )}

          {entry.carriedFromMinutesId && (
            <span className="np-chip np-tone-info" title={`Carried forward from ${entry.carriedFromMinutesId}`}>
              <i className="ri-corner-down-right-line"></i>
              Carried from <span className="np-tabular">{entry.carriedFromMinutesId}</span>
            </span>
          )}

          {entry.promotedArtefactId &&
            (artefactRoute ? (
              <Link
                className="np-chip np-tone-dark"
                to={`${artefactRoute}/${entry.promotedArtefactId}`}
                title="Open the artefact raised from this entry"
              >
                <i className="ri-external-link-line"></i>
                <span className="np-tabular">{entry.promotedArtefactId}</span>
              </Link>
            ) : (
              <span className="np-chip np-tone-dark">
                <i className="ri-links-line"></i>
                <span className="np-tabular">{entry.promotedArtefactId}</span>
              </span>
            ))}
        </>
      }
    >
      {/* ── Category ─────────────────────────────────────────────────────────── */}
      <fieldset className="np-fieldset mb-3">
        <legend className="np-field-caption">What kind of entry is this?</legend>
        <div className="np-pill-toggle-group">
          {MINUTE_CATEGORIES.map((c) => {
            const selected = entry.category === c;
            return (
              <button
                key={c}
                type="button"
                className={`np-pill-toggle${selected ? ` active np-tone-${MINUTE_CATEGORY_TONE[c]}` : ''}`}
                aria-pressed={selected}
                disabled={readOnly}
                onClick={() =>
                  // Leaving 'action' drops the ownership cluster from the UI; clearing it too
                  // stops a stale owner/date being persisted on, say, a decision.
                  onChange(
                    categoryTakesOwner(c)
                      ? { category: c }
                      : { category: c, ownerId: '', dueDate: '', status: 'open' }
                  )
                }
              >
                <i className={MINUTE_CATEGORY_ICON[c]}></i> {MINUTE_CATEGORY_LABELS[c]}
              </button>
            );
          })}
        </div>
        <p className="np-hint">{MINUTE_CATEGORY_HINT[entry.category]}</p>
      </fieldset>

      {/* ── The entry itself ─────────────────────────────────────────────────── */}
      <div className="np-field">
        {/* No htmlFor: the control is a Quill editor, not a labellable form element. */}
        <label>What was said or agreed</label>
        {readOnly ? (
          <RichTextView value={entry.body} empty="No detail was recorded for this entry." />
        ) : (
          <RichTextField
            value={entry.body}
            onChange={(body) => onChange({ body })}
            placeholder={
              takesOwner
                ? 'e.g. Draft the migration runbook and circulate it to the platform team.'
                : 'e.g. Agreed to keep the existing supplier until the contract review in Q3.'
            }
            height={110}
          />
        )}
      </div>

      {/* ── Ownership — actions only ─────────────────────────────────────────── */}
      {takesOwner && (
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-5">
            <div className="np-field mb-0">
              <label htmlFor={`entry-owner-${entry.entryId}`}>Owner</label>
              <Select
                inputId={`entry-owner-${entry.entryId}`}
                options={people}
                value={ownerValue}
                onChange={(opt) => onChange({ ownerId: opt ? opt.value : '' })}
                placeholder="Who has agreed to do this?"
                isDisabled={readOnly}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles(readOnly)}
              />
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="np-field mb-0">
              <label htmlFor={`entry-due-${entry.entryId}`}>Due by</label>
              <input
                id={`entry-due-${entry.entryId}`}
                type="date"
                className="np-input"
                value={entry.dueDate}
                disabled={readOnly}
                onChange={(e) => onChange({ dueDate: e.target.value })}
              />
            </div>
          </div>

          <div className="col-12 col-md-3">
            <span className="np-field-caption">Progress</span>
            <div className="d-flex align-items-center gap-2">
              <label className="np-switch mb-0" htmlFor={`entry-done-${entry.entryId}`}>
                <input
                  type="checkbox"
                  id={`entry-done-${entry.entryId}`}
                  checked={entry.status === 'done'}
                  disabled={readOnly}
                  onChange={(e) => onChange({ status: e.target.checked ? 'done' : 'open' })}
                />
                <span className="np-switch-track"></span>
                <span className="np-switch-thumb"></span>
              </label>
              <label htmlFor={`entry-done-${entry.entryId}`} className="mb-0 small">
                {entry.status === 'done' ? 'Done' : 'Still open'}
              </label>
            </div>
          </div>
        </div>
      )}

      {/* ── Links into the initiative's plan ─────────────────────────────────── */}
      {canLink ? (
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="np-field mb-0">
              <label htmlFor={`entry-reqs-${entry.entryId}`}>Related requirements</label>
              <Select
                inputId={`entry-reqs-${entry.entryId}`}
                options={requirements}
                value={findOptions(requirements, entry.requirementIds)}
                onChange={(opts) => onChange({ requirementIds: opts.map((o) => o.value) })}
                placeholder={requirements.length ? 'Link to requirements…' : 'No requirements on this initiative'}
                isDisabled={readOnly || requirements.length === 0}
                isMulti
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles(readOnly || requirements.length === 0)}
              />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="np-field mb-0">
              <label htmlFor={`entry-miles-${entry.entryId}`}>Related milestones</label>
              <Select
                inputId={`entry-miles-${entry.entryId}`}
                options={milestones}
                value={findOptions(milestones, entry.milestoneIds)}
                onChange={(opts) => onChange({ milestoneIds: opts.map((o) => o.value) })}
                placeholder={milestones.length ? 'Link to milestones…' : 'No milestones on this initiative'}
                isDisabled={readOnly || milestones.length === 0}
                isMulti
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles(readOnly || milestones.length === 0)}
              />
            </div>
          </div>
        </div>
      ) : (
        <p className="np-hint mt-0">
          <i className="ri-links-line"></i> Pin this meeting to an initiative to link entries to its
          requirements and milestones.
        </p>
      )}

      {/* ── Linked initiatives — cross-cutting: independent of whether this meeting is
          pinned to any one initiative, so an entry can matter to several at once ────── */}
      {canLinkInitiatives && (
        <div className="np-field mt-3 mb-0">
          <label htmlFor={`entry-inits-${entry.entryId}`}>Linked initiatives</label>
          <Select
            inputId={`entry-inits-${entry.entryId}`}
            options={initiatives}
            value={findOptions(initiatives, entry.initiativeIds)}
            onChange={(opts) => onChange({ initiativeIds: opts.map((o) => o.value) })}
            placeholder="Link to one or more portfolios, programs or projects…"
            isDisabled={readOnly}
            isMulti
            isSearchable
            menuPortalTarget={document.body}
            styles={reactSelectStyles(readOnly)}
          />
        </div>
      )}

      {/* ── Escalation ───────────────────────────────────────────────────────── */}
      {!readOnly && !entry.promotedArtefactId && PROMOTABLE.has(entry.category) && (
        <div className="d-flex align-items-center gap-2 mt-3">
          <button
            type="button"
            className="np-btn np-btn-ghost np-btn-sm"
            onClick={() => onPromote(entry)}
            disabled={isRichTextEmpty(entry.body)}
            title={
              isRichTextEmpty(entry.body)
                ? 'Write the entry first — its text seeds the risk'
                : 'Open the risk register with this entry pre-filled'
            }
          >
            <i className="ri-error-warning-line"></i>Raise as risk
          </button>
          <span className="np-hint m-0">
            A {MINUTE_CATEGORY_LABELS[entry.category].toLowerCase()} noted in a meeting is not tracked
            anywhere until it is raised on the risk register.
          </span>
        </div>
      )}
    </RepeatableRowCard>
  );
};

export default MinuteEntryRow;
